import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { HoaInfo } from '../../types';
import { isYes, RequiredMark } from '../../lib/requiredFields';

export default function Section8HOA() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<HoaInfo>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.hoa_info) {
      setData(state.formData.hoa_info);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.hoa_info, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.intake && data.intake_id) {
      saveSection('hoa_info', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof HoaInfo | string, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isReq = (key: string) =>
    state.requiredFields.some(f => f.section === 'hoa_info' && f.field_key === key);

  const hasHoaYes = isYes(data.has_hoa);
  const detailRequired = (key: string) => hasHoaYes && isReq(key);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">HOA Information</h2>
        <p className="text-sm text-gray-600 mb-6">
          Fields marked with <span className="text-brand-coral">*</span> are required
          {hasHoaYes ? ' (including HOA details below)' : ''}.
        </p>

        <div className="space-y-6">
          {/* Has HOA */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Does your property have a Homeowners Association (HOA)?
              {isReq('has_hoa') && <RequiredMark />}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="has_hoa"
                  checked={data.has_hoa === true || data.has_hoa === 1}
                  onChange={() => updateField('has_hoa', true)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="has_hoa"
                  checked={data.has_hoa === false || data.has_hoa === 0}
                  onChange={() => updateField('has_hoa', false)}
                  className="mr-2"
                />
                No
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="has_hoa"
                  checked={data.has_hoa === 'unsure'}
                  onChange={() => updateField('has_hoa', 'unsure')}
                  className="mr-2"
                />
                Not sure
              </label>
            </div>
          </div>

          {/* HOA Details - only show if has_hoa is true */}
          {hasHoaYes && (
            <div className="space-y-6 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900">HOA Details</h3>

              {/* Association Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Association Name{detailRequired('association_name') && <RequiredMark />}
                </label>
                <input
                  type="text"
                  value={data.association_name || ''}
                  onChange={(e) => updateField('association_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="e.g., Oak Valley Homeowners Association"
                />
              </div>

              {/* HOA Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HOA Fee
                  {(detailRequired('hoa_fee') || detailRequired('hoa_fee_frequency')) && <RequiredMark />}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      value={data.hoa_fee || ''}
                      onChange={(e) => updateField('hoa_fee', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      placeholder="e.g., $150"
                    />
                  </div>
                  <div>
                    <select
                      value={(data as any).hoa_fee_frequency || ''}
                      onChange={(e) => updateField('hoa_fee_frequency' as any, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    >
                      <option value="">Select frequency</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                      <option value="other">Other</option>
                    </select>
                    {(data as any).hoa_fee_frequency === 'other' && (
                      <input
                        type="text"
                        value={(data as any).hoa_fee_frequency_other || ''}
                        onChange={(e) => updateField('hoa_fee_frequency_other' as any, e.target.value)}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        placeholder="Please specify frequency"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* What HOA Includes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What is included in the HOA charges:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Grounds Care', 'Trash Removal', 'Snow Removal', 'Hot Water', 'Water',
                    'Taxes', 'Security Service', 'Front Desk Receptionist', 'Heat',
                    'Air Conditioning', 'Sewer', 'Pest Control', 'Road Maintenance',
                    'Insurance', 'Garage Parking',
                  ].map((item) => {
                    const list: string[] = (data as any).hoa_includes_list || [];
                    return (
                      <label key={item} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={list.includes(item)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...list, item]
                              : list.filter((i: string) => i !== item);
                            setData(prev => ({
                              ...prev,
                              hoa_includes_list: updated,
                              hoa_includes: updated.join(', '),
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* HOA Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Common Amenities:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Cabana', 'Club House', 'Laundry Facilities', 'Pool', 'Security System',
                    'Tennis Court', 'Exercise Room', 'Paddle Court', 'Sauna',
                  ].map((item) => {
                    const list: string[] = (data as any).hoa_amenities_list || [];
                    return (
                      <label key={item} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={list.includes(item)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...list, item]
                              : list.filter((i: string) => i !== item);
                            setData(prev => ({
                              ...prev,
                              hoa_amenities_list: updated,
                              hoa_amenities: updated.join(', '),
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{item}</span>
                      </label>
                    );
                  })}
                  <div className="flex items-center gap-2 col-span-full">
                    <span className="text-sm text-gray-700">Other:</span>
                    <input
                      type="text"
                      value={(data as any).hoa_amenities_other || ''}
                      onChange={(e) => updateField('hoa_amenities_other' as any, e.target.value)}
                      className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              {/* Pets Allowed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Are pets allowed?{detailRequired('pets_allowed') && <RequiredMark />}
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pets_allowed"
                      checked={data.pets_allowed === true || data.pets_allowed === 1}
                      onChange={() => updateField('pets_allowed', true)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pets_allowed"
                      checked={data.pets_allowed === false || data.pets_allowed === 0}
                      onChange={() => updateField('pets_allowed', false)}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pets_allowed"
                      checked={data.pets_allowed === 'restricted'}
                      onChange={() => updateField('pets_allowed', 'restricted')}
                      className="mr-2"
                    />
                    Restricted (size/breed limits)
                  </label>
                </div>
              </div>

              {/* Other HOA Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other HOA Information
                </label>
                <textarea
                  value={data.other_hoa_info || ''}
                  onChange={(e) => updateField('other_hoa_info', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  rows={4}
                  placeholder="Any additional HOA information such as special assessments, upcoming projects, architectural restrictions, etc."
                />
              </div>

              {/* HOA Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HOA Management Company (if applicable)
                  </label>
                  <input
                    type="text"
                    value={(data as any).hoa_management_company || ''}
                    onChange={(e) => updateField('hoa_management_company' as any, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Management company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HOA Contact Phone/Email
                  </label>
                  <input
                    type="text"
                    value={(data as any).hoa_contact || ''}
                    onChange={(e) => updateField('hoa_contact' as any, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="Phone number or email"
                  />
                </div>
              </div>
            </div>
          )}

          {/* No HOA message */}
          {(data.has_hoa === false || data.has_hoa === 0) && (
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                No HOA information needed. This can be helpful information for buyers who prefer
                properties without HOA restrictions or fees.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            HOA information is important for buyers to understand any additional costs, restrictions,
            or benefits associated with the property. If you&apos;re not sure about these details, we can help
            you find the information.
          </p>
        </div>
      </div>
    </div>
  );
}
